import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import {
  ConversationTimelinePage,
  getConversationTimelineQueryKey,
} from "modules/conversations/lib/conversationTimelineQuery"
import { getConversationQueryKey } from "modules/conversations/lib/conversationQuery"
import { getMessagesQueryKey, MessagesPage } from "modules/messages/lib/messagesQuery"
import {
  ConversationUploadedFileSchema,
  DataResponse,
  MessageFormatEnum,
  MessageResponseDataSchema,
  MessageRoleEnum,
} from "schema"
import { z } from "zod"

type Params = {
  conversationId: string
  content?: string
  uploadedFiles?: UploadedConversationFile[]
}

type Props = {
  onError?: (error: Error, variables: Params) => void
}

type MessageResponse = z.infer<typeof MessageResponseDataSchema>
type UploadedConversationFile = z.infer<typeof ConversationUploadedFileSchema> & {
  previewUrl?: string
}
type Response = DataResponse<MessageResponse | MessageResponse[]>

export function useCreateMessageMutation(props: Props) {
  const queryClient = useQueryClient()
  const { data: employee } = useAuthEmployeeQuery()

  return useMutation<Response, Error, Params>({
    mutationFn: async (params) => {
      const uploadedFiles = params.uploadedFiles?.map(({ mediaUrl, format }) => ({
        mediaUrl,
        format,
      }))

      const response = await axios.post<Response>(
        `/conversations/${params.conversationId}/messages`,
        {
          content: params.content || undefined,
          uploadedFiles: uploadedFiles?.length ? uploadedFiles : undefined,
        },
      )

      return response.data
    },
    onMutate: async (newMessage) => {
      const timelineKey = getConversationTimelineQueryKey(newMessage.conversationId)
      const messagesKey = getMessagesQueryKey(newMessage.conversationId)
      const uploadedFiles = newMessage.uploadedFiles ?? []
      const now = new Date()
      const optimisticId = crypto.randomUUID()
      const optimisticMessages: z.infer<typeof MessageResponseDataSchema>[] = uploadedFiles.length
        ? uploadedFiles.map((image, index) => ({
            id: `temp-${optimisticId}-${index}`,
            createdAt: now.toISOString(),
            content: index === 0 ? (newMessage.content ?? null) : null,
            role: MessageRoleEnum.Assistant,
            conversationId: newMessage.conversationId,
            format: image.format,
            mediaUrl: image.previewUrl ?? image.mediaUrl,
            employee: employee
              ? {
                  id: employee.id,
                  name: employee.name,
                  email: employee.email,
                }
              : undefined,
          }))
        : [
            {
              id: `temp-${optimisticId}`,
              createdAt: now.toISOString(),
              content: newMessage.content ?? "",
              role: MessageRoleEnum.Assistant,
              conversationId: newMessage.conversationId,
              format: MessageFormatEnum.Text,
              mediaUrl: null,
              employee: employee
                ? {
                    id: employee.id,
                    name: employee.name,
                    email: employee.email,
                  }
                : undefined,
            },
          ]

      queryClient.setQueryData<InfiniteData<ConversationTimelinePage>>(timelineKey, (oldData) => {
        if (!oldData || oldData.pages.length === 0) return oldData
        const firstPage = oldData.pages[0]!
        const newestMessageId = optimisticMessages.at(-1)?.id ?? firstPage.pageInfo.newestMessageId
        const pages = [
          {
            ...firstPage,
            messages: [...firstPage.messages, ...optimisticMessages],
            pageInfo: {
              ...firstPage.pageInfo,
              newestMessageId,
            },
          },
          ...oldData.pages.slice(1),
        ]
        return { ...oldData, pages }
      })

      queryClient.setQueryData<InfiniteData<MessagesPage>>(messagesKey, (oldData) => {
        if (!oldData || oldData.pages.length === 0) return oldData
        const firstPage = oldData.pages[0]!
        const newestId = optimisticMessages.at(-1)?.id ?? firstPage.pageInfo.newestId
        const pages = [
          {
            ...firstPage,
            messages: [...firstPage.messages, ...optimisticMessages],
            pageInfo: {
              ...firstPage.pageInfo,
              newestId,
            },
          },
          ...oldData.pages.slice(1),
        ]
        return { ...oldData, pages }
      })

      return { optimisticMessageIds: optimisticMessages.map((message) => message.id) }
    },
    onError: (err, newMessage, context) => {
      const typedContext = context as { optimisticMessageIds: string[] } | undefined
      props.onError?.(err, newMessage)
      if (!typedContext) return
      const failedIds = new Set(typedContext.optimisticMessageIds)

      queryClient.setQueryData<InfiniteData<ConversationTimelinePage>>(
        getConversationTimelineQueryKey(newMessage.conversationId),
        (data) =>
          data && {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              messages: page.messages.filter((message) => !failedIds.has(message.id)),
            })),
          },
      )
      queryClient.setQueryData<InfiniteData<MessagesPage>>(
        getMessagesQueryKey(newMessage.conversationId),
        (data) =>
          data && {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              messages: page.messages.filter((message) => !failedIds.has(message.id)),
            })),
          },
      )
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: getConversationTimelineQueryKey(variables.conversationId),
      })
      queryClient.invalidateQueries({
        queryKey: getMessagesQueryKey(variables.conversationId),
      })
      queryClient.invalidateQueries({
        queryKey: getConversationQueryKey(variables.conversationId),
      })
    },
  })
}
